import {
  Button,
  Flex,
  TextInput,
  TextInputProps,
} from '@utilitywarehouse/hearth-react';
import { FieldArrayRenderProps, FormikProps, getIn } from 'formik';

import { SaveFormValues, ValidateSaveFormResult } from '../types';
import { hasNoErrors } from './validate';

enum ArrayFieldName {
  mobiles = 'mobiles',
  emails = 'emails',
}

type ArrayFieldProps = FieldProps & {
  values: string[];
  name: ArrayFieldName;
};

type FieldProps = Omit<FieldArrayRenderProps, 'form'> & {
  isLoading: boolean;
  form: FormikProps<SaveFormValues>;
};

type FieldBindingProps = {
  form: FormikProps<SaveFormValues>;
  fieldName: string;
  value: string;
};

const getInputType = (name: ArrayFieldName) =>
  name === ArrayFieldName.mobiles ? 'tel' : 'email';

const getInputLabel = (name: ArrayFieldName, index: number) => {
  const label =
    name === ArrayFieldName.mobiles ? 'Mobile number' : 'Email address';
  return index === 0 ? label : '';
};

const getButtonLabels = (name: ArrayFieldName) => {
  if (name === ArrayFieldName.mobiles) {
    return {
      addLabel: 'Add another mobile number',
    };
  }

  return {
    addLabel: 'Add another email address',
  };
};

const getFieldProps = ({ form, fieldName, value }: FieldBindingProps) => {
  return {
    id: fieldName,
    name: fieldName,
    value,
    onChange: form.handleChange,
    onBlur: form.handleBlur,
  };
};
const isAllFieldsValid = (
  errors: ValidateSaveFormResult,
  name: ArrayFieldName
) => {
  const arrayFieldErrors = errors[name];

  if (!arrayFieldErrors) {
    return true;
  }

  return hasNoErrors(arrayFieldErrors);
};

const getFieldState = (
  form: FormikProps<SaveFormValues>,
  fieldName: string
) => {
  const error = getIn(form.errors, fieldName);
  const touched = getIn(form.touched, fieldName);

  const hasError = Boolean(touched && error);

  return {
    error,
    hasError,
  };
};

type ArrayFieldItemProps = {
  form: FormikProps<SaveFormValues>;
  name: ArrayFieldName;
  index: number;
  value: string;
  isLoading: boolean;
  hasMultipleFields: boolean;
  remove: (index: number) => void;
};

const ArrayFieldItem = ({
  form,
  name,
  index,
  value,
  isLoading,
  hasMultipleFields,
  remove,
}: ArrayFieldItemProps) => {
  const fieldName = `${name}.${index}`;
  const { error, hasError } = getFieldState(form, fieldName);

  const validationStatus: TextInputProps['validationStatus'] = hasError
    ? 'invalid'
    : !error && value
      ? 'valid'
      : undefined;

  const inputType = getInputType(name);

  return (
    <Flex
      spacing="sm"
      alignItems={{
        desktop: hasError ? 'start' : 'end',
        tablet: hasError ? 'start' : 'end',
        mobile: 'start',
      }}
      direction={{ mobile: 'column', desktop: 'row', tablet: 'row' }}
    >
      <TextInput
        required
        data-testid={`save-for-later-${inputType}-input`}
        className="saveForLater__textField"
        type={inputType}
        label={getInputLabel(name, index)}
        disabled={isLoading}
        {...getFieldProps({
          form,
          fieldName,
          value,
        })}
        validationStatus={validationStatus}
        validationText={hasError ? error : undefined}
      />

      {hasMultipleFields && (
        <Button
          size="md"
          variant="ghost"
          colorScheme="destructive"
          paddingNone
          onClick={() => remove(index)}
          disabled={isLoading}
        >
          Remove
        </Button>
      )}
    </Flex>
  );
};

const ArrayField = ({
  form,
  isLoading,
  values,
  name,
  remove,
  insert,
}: ArrayFieldProps) => {
  const { errors } = form;
  const hasMultipleFields = values.length > 1;
  const nextValueIndex = values.length;
  const primaryValue = values[0];
  const { addLabel } = getButtonLabels(name);

  const addDisabled =
    isLoading ||
    // Only one contact detail is required
    // The second remains valid even if it is empty.
    !primaryValue ||
    !isAllFieldsValid(errors as ValidateSaveFormResult, name);

  return (
    <Flex direction="column" spacing="xl">
      <Flex direction="column" spacing="xl">
        {values.map((value, index) => (
          <ArrayFieldItem
            key={`${name}.${index}`}
            form={form}
            name={name}
            index={index}
            value={value}
            isLoading={isLoading}
            hasMultipleFields={hasMultipleFields}
            remove={remove}
          />
        ))}
      </Flex>

      <Flex alignItems="start">
        <Button
          size="sm"
          variant="ghost"
          colorScheme="functional"
          paddingNone
          onClick={() => insert(nextValueIndex, '')}
          disabled={addDisabled}
        >
          {addLabel}
        </Button>
      </Flex>
    </Flex>
  );
};

const MobileFields = (props: FieldProps) => (
  <ArrayField
    {...props}
    name={ArrayFieldName.mobiles}
    values={props.form.values.mobiles}
  />
);

const EmailFields = (props: FieldProps) => (
  <ArrayField
    {...props}
    name={ArrayFieldName.emails}
    values={props.form.values.emails}
  />
);

export { MobileFields, EmailFields };
